import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { cn } from "@/lib/utils";
import { setProjectPhaseStatus } from "@/api/actions/project-log";
import { completionAsksReason, MAX_SCHEDULE_REASON, type SchedulePhase } from "@buildpy/core/modules/project/phase-schedule";
import { PHASE_TRANSITIONS } from "@buildpy/core/modules/project/transitions";
import { PROJECT_PHASE_STATUS_LABEL, type ProjectPhaseStatus } from "@buildpy/core/modules/project/types";
import { useRecordAction } from "@/modules/project/ui/use-record-action";
import { todayKst } from "@buildpy/core/modules/project/view";

// 누르면 무엇이 되는지 — 되돌리기는 「되돌리기」로 읽힌다.
const ACTION_LABEL: Record<ProjectPhaseStatus, string> = {
  PENDING: "대기로",
  ACTIVE: "진행",
  DONE: "완료",
  HOLD: "보류",
};

interface Props {
  projectId: string;
  phaseId: string;
  status: ProjectPhaseStatus;
  phaseName: string;
  // 계획 날짜·실적 — 주면 완료를 누른 순간의 KST 오늘로 계획 끝날과 다른지 보고 사유(선택)를 묻는다.
  schedule?: SchedulePhase;
  className?: string;
  // testid 앞부분 — 표와 폰 카드가 한 화면에 같이 있을 때 카드 쪽이 겹치지 않게 바꾼다.
  testIdPrefix?: string;
}

// 공정 상태 버튼 묶음 — 전이표가 허락하는 다음 상태만 보인다. from 을 같이 보내 낡은 화면을 막는다.
const PhaseStatusControl = ({ projectId, phaseId, status, phaseName, schedule, className, testIdPrefix }: Props) => {
  const tid = testIdPrefix ?? `phase-${phaseId}`;
  const { pending, run } = useRecordAction();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");

  const change = (to: ProjectPhaseStatus, withReason?: string) =>
    void run(
      "공정 상태를 바꾸지 못했습니다",
      () =>
        setProjectPhaseStatus(projectId, {
          phase_id: phaseId,
          from: status,
          to,
          ...(withReason ? { reason: withReason } : {}),
        }),
      () => {
        setAsking(false);
        setReason("");
        toast.success(`${phaseName} — ${PROJECT_PHASE_STATUS_LABEL[to]}`);
      }
    );

  return (
    <div aria-label={`${phaseName} 상태 바꾸기`} className={cn("flex flex-wrap gap-1", className)} role="group">
      {PHASE_TRANSITIONS[status].map((to) => (
        <button
          key={to}
          className={cn(
            "min-h-8 rounded-md border px-2.5 text-xs font-medium disabled:opacity-50",
            to === "DONE"
              ? "border-status-done text-status-done"
              : to === "ACTIVE"
                ? "border-status-progress text-status-progress"
                : "border-border text-muted-foreground"
          )}
          data-testid={`${tid}-to-${to}`}
          disabled={pending || asking}
          type="button"
          onClick={() =>
            to === "DONE" && schedule && completionAsksReason(schedule, todayKst()) ? setAsking(true) : change(to)
          }>
          {ACTION_LABEL[to]}
        </button>
      ))}
      {asking && (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-1" data-testid={`${tid}-reason-ask`}>
          <div className="min-w-40 flex-1">
            <Input
              aria-label={`${phaseName} 계획과 다른 이유`}
              disabled={pending}
              inputSize="xs"
              maxLength={MAX_SCHEDULE_REASON}
              name={`phase-${phaseId}-reason`}
              placeholder="계획과 다른 이유(선택)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <Button
            data-testid={`${tid}-done-confirm`}
            disabled={pending}
            size="sm"
            type="button"
            onClick={() => change("DONE", reason.trim())}>
            완료
          </Button>
          <Button disabled={pending} size="sm" type="button" variant="ghost" onClick={() => setAsking(false)}>
            취소
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhaseStatusControl;
